import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from "react-toastify";
import AppButton from "../../Components/MerchantButton";
import "./UnifiedFormStyles.css";
import { submitForm } from "../../Helpers/API/formsAPI";
import { getRegionConfig } from "./config/regionConfig";
import { compressAndUpload } from "../../Helpers/Utils/photoUpload";

export default function Remarks({ navigation, primaryDetails = {}, setPrimaryDetails, onBack, onNext }) {
  const navigate = useNavigate();
  // Local UI error state only
  const [errors, setErrors] = useState({ file: false, duration: false });
  const [backendResponse, setBackendResponse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Set current time as default for visit_duration if not already set
  useEffect(() => {
    if (!primaryDetails?.visit_duration && setPrimaryDetails) {
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 5); // Format: HH:MM
      setPrimaryDetails(prev => ({
        ...prev,
        visit_duration: currentTime
      }));
    }
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      setPrimaryDetails(prev => ({
        ...prev,
        store_photo_temp_path: uploadResult.temp_path
      }));
      if (errors.file) setErrors(prev => ({ ...prev, file: false }));
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  const validate = () => {
    const next = {
      file: !primaryDetails?.store_photo_temp_path,
      duration: !primaryDetails?.visit_duration,
    };
    setErrors(next);
    if (next.file || next.duration) {
      toast.error("Please attach a photo and set visit duration.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    return !!primaryDetails?.store_photo_temp_path && !!primaryDetails?.visit_duration;
  }
  const handleBack = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (onBack) return onBack();
    navigation.previous();
  };

  const handleNext = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validate()) return;
    
    // Show confirmation modal first
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);

    try {
      const regionConfig = getRegionConfig(primaryDetails?.region);
      const res = await submitForm(primaryDetails, regionConfig);

      if (res?.data) {
        setBackendResponse(res.data);
        // Clear all form data on successful submit
        try {
          localStorage.removeItem('maya-form-data');
          localStorage.removeItem('maya-form-details');
          localStorage.removeItem('maya-form-current-step');
          localStorage.removeItem('maya-form-history');
          localStorage.setItem('form_completed', 'true');
        } catch (e) {}
        setShowConfirmModal(false);
        setShowSuccessModal(true);
      } else {
        toast.error("Submit failed. Please try again.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
      }
    } catch (err) {
      toast.error("Something went wrong while submitting.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
    if (navigation && navigation.go) {
      navigation.go('promotional-activity');
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/timein', { replace: true });
  };


  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">REMARKS</h4>
              
            </div>

            <div className="form-main">
              <div className="form-main-content">
                <Form>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Storefront Visit Photo <span className="form-required">*</span>
                      {primaryDetails?.store_photo_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      isInvalid={errors.file}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {errors.file && (
                      <div className="form-error">Required</div>
                    )}
                    <div>
                      <p className="form-note-text">
                        <em>"Note: Selfie photo in front of merchant store"</em>
                      </p>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="remarks">
                    <Form.Label className="form-label">Remarks</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder="Long answer text"
                      value={primaryDetails?.remarks || ''}
                      onChange={(e) => setPrimaryDetails && setPrimaryDetails(prev => ({
                      ...prev,
                      remarks: e.target.value,
                    }))}
                      className="form-input"
                      rows={3}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="duration">
                    <Form.Label className="form-label">
                      Duration of visit <span className="form-required">*</span>
                    </Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type="time"
                        value={primaryDetails?.visit_duration || ''}
                        onChange={(e) => {
                          if (setPrimaryDetails) {
                            setPrimaryDetails(prev => ({
                            ...prev,
                            visit_duration: e.target.value,
                          }));
                          }
                          if (errors.duration) setErrors((prev) => ({ ...prev, duration: false }));
                        }}
                        className={`form-input${errors.duration ? ' form-input-error' : ''}`}
                      />
                      <span className="input-group-text" aria-hidden="true">🕒</span>
                    </div>
                    {errors.duration && (
                      <div className="form-error">Please set the visit duration.</div>
                    )}
                  </Form.Group>

                  <div className="form-btn-row">
                    <AppButton type="button" onClick={handleBack} variant="back" className="form-btn variant-secondary">
                      ← Back
                    </AppButton>
                    <AppButton type="button" onClick={handleNext} variant="next" className="form-btn variant-primary" disabled={!isFormValid()}>
                      Submit
                    </AppButton>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>

        {/* Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={handleCancelSubmit} centered>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100 text-center fw-bold text-primary">
              ⚠️ Confirm Submission
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <div className="mb-3">
              <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
            </div>
            <p className="text-muted mb-0"><strong>Are you sure you want to Submit or Go to Promotional activity?</strong></p>
            <p className="text-muted mb-0">
              <strong>Once submitted, you can't go back to make changes.</strong>
            </p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={handleCancelSubmit}
              className="px-4 me-3"
              style={{minWidth: '100px'}}
              disabled={isSubmitting}
            >
              Proceed to Promotional Activity
            </Button>
            <Button 
              variant="primary" 
              onClick={handleConfirmSubmit}
              className="px-4"
              style={{minWidth: '100px', backgroundColor: '#007bff'}}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Submitting...
                </>
              ) : (
                'Yes, Submit'
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Success Modal */}
        <Modal show={showSuccessModal} onHide={handleSuccessModalClose} centered>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100 text-center fw-bold text-success">
              ✅ Form Successfully Submitted
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <div className="mb-3">
              <i className="fas fa-check-circle text-success" style={{fontSize: '3rem'}}></i>
            </div>
            <p className="text-muted mb-0"><strong>Your form has been successfully saved!</strong></p>
            <p className="text-muted mb-0">
              Thank you for completing the form. Your data has been securely stored.
            </p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center pt-0">
            <Button 
              variant="success" 
              onClick={handleSuccessModalClose}
              className="px-4"
              style={{minWidth: '120px'}}
            >
              Continue
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
